import { useEffect, useState } from "react";
import { geoServer } from "../services/geoServer";
import { palette, hashIndex } from "../global/layerStyles/apl_pe_observatorio";
import { mockLayers } from "../global/constants/layers";
import { CATEGORICAL_LEGEND_FIELDS } from "../global/constants/categoricalLegends";

export interface CategoricalLegendItem {
  label: string;
  color: string;
}

/**
 * Monta a legenda de uma camada pintada por hash: lê os valores distintos do
 * campo declarado em `CATEGORICAL_LEGEND_FIELDS` e devolve um item por valor,
 * com a mesma cor que o estilo aplica no mapa.
 */
export function useCategoricalLegend(
  layerName: string,
  enabled: boolean
): CategoricalLegendItem[] {
  const [items, setItems] = useState<CategoricalLegendItem[]>([]);
  const field = CATEGORICAL_LEGEND_FIELDS[layerName];
  const layer = mockLayers.find((l) => l.layerName === layerName);
  const typeName = `${layer?.workspace ?? "pepe-atlas"}:${layerName}`;

  useEffect(() => {
    if (!enabled || !field) return;
    let cancelled = false;

    // Sem bbox nem maxFeatures de propósito: a legenda precisa de todas as
    // classes da camada, não só das visíveis. `propertyName` deixa a geometria
    // fora da resposta, então o payload fica pequeno.
    geoServer
      .get("wfs", {
        params: {
          service: "WFS",
          version: "1.0.0",
          request: "GetFeature",
          typeName,
          outputFormat: "application/json",
          propertyName: field,
        },
      })
      .then((res) => {
        if (cancelled) return;
        const features: any[] = res.data?.features ?? [];
        const seen = new Set<string>();
        features.forEach((f) => {
          const value: string = f.properties?.[field];
          if (value) seen.add(value);
        });
        const sorted = Array.from(seen).sort((a, b) =>
          a.localeCompare(b, "pt-BR")
        );
        setItems(
          sorted.map((label) => ({ label, color: palette[hashIndex(label)] }))
        );
      })
      .catch(() => {
        // silently fail — legend simply won't list the layer's classes
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, field, typeName]);

  return items;
}
